/*
 * Legacy customer history SINK 3 — the per-event drill-down (backlog 1g;
 * LOCKED PROGRAMME-DEC-LEGACY-CUSTOMER-MIGRATION-V1 clause 3, D1: the results
 * detail stays in the lake and my-api looks it up on demand).
 *
 * A "See your past events" control on the Orders "Earlier with Trumin" card
 * that expands IN PLACE (the Past events toggle pattern). The list is fetched
 * on the FIRST tap only — never on page load, so the sink 2 overview never
 * waits for it — then kept for the life of the card. States: loading, list,
 * empty, unavailable (with Try again).
 *
 * Flag: VITE_LEGACY_HISTORY_EVENTS_ENABLED === 'true' (else nothing renders).
 * Each item: event name, date, organisation / unit, category / heat, status
 * (computed server-side: result > check-in > registration), finish time
 * h:mm:ss, "Rank N of field" and "1 of N results" when the entry has several
 * results (the best one is shown).
 */
import { useState, type CSSProperties } from 'react'
import { loadSession } from '../auth/session'
import { useLocale, type TranslateFn } from '../i18n/useLocale'
import type { MessageKey } from '../i18n/en'
import { formatFinishTime, formatPlainDate } from '../data/legacyHistory'
import { fetchMyLegacyEvents, myApiBase, type LegacyEventStatus, type MyLegacyEvent } from '../data/myApi'
import { legacyHistoryEventsEnabled } from '../lib/legacyHistoryFlag'

type State = { kind: 'closed' } | { kind: 'loading' } | { kind: 'ok'; events: MyLegacyEvent[] } | { kind: 'unavailable' }

const STATUS_KEY: Record<LegacyEventStatus, MessageKey> = {
  finished: 'history.events.status.finished',
  took_part: 'history.events.status.tookPart',
  did_not_finish: 'history.events.status.didNotFinish',
  registered: 'history.events.status.registered',
}

const linkButton: CSSProperties = {
  border: 0, background: 'transparent', padding: 0, cursor: 'pointer', fontFamily: 'inherit',
  fontSize: 12.5, fontWeight: 700, color: 'var(--tru-status-teal-bright)', textAlign: 'start',
}
const quiet: CSSProperties = { fontSize: 12, color: 'var(--tru-text-secondary)', lineHeight: 1.5, margin: '10px 0 0' }
const meta: CSSProperties = { display: 'block', fontSize: 11.5, color: 'var(--tru-text-tertiary)', lineHeight: 1.45 }

const joinParts = (parts: Array<string | null>): string | null => {
  const p = parts.filter((x): x is string => !!x)
  return p.length ? p.join(' · ') : null
}

function EventItem({ e, t }: { e: MyLegacyEvent; t: TranslateFn }) {
  const { lang } = useLocale()
  const n = (v: number) => v.toLocaleString(lang === 'ar' ? 'ar' : 'en-GB')
  const date = e.event_date
    ? e.event_end_date
      ? `${formatPlainDate(e.event_date, lang)} – ${formatPlainDate(e.event_end_date, lang)}`
      : formatPlainDate(e.event_date, lang)
    : null
  const org = joinParts([e.organisation_name, e.org_unit_name !== e.organisation_name ? e.org_unit_name : null])
  const entry = joinParts([e.category_name, e.heat_name])
  const finished = e.status === 'finished'
  return (
    <li data-testid="legacy-event" data-status={e.status} style={{ padding: '10px 0', borderBlockStart: '1px solid var(--hairline-08)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline' }}>
        <span data-testid="legacy-event-name" style={{ fontSize: 13.5, fontWeight: 800 }}>{e.event_name ?? t('history.events.unnamed')}</span>
        <span data-testid="legacy-event-status" style={{ fontSize: 11.5, fontWeight: 700, color: finished ? 'var(--tru-status-teal-bright)' : 'var(--tru-text-secondary)', whiteSpace: 'nowrap' }}>
          {t(STATUS_KEY[e.status])}
        </span>
      </div>
      {date && <span data-testid="legacy-event-date" style={meta}>{date}</span>}
      {org && <span data-testid="legacy-event-org" style={meta}>{org}</span>}
      {entry && <span data-testid="legacy-event-entry" style={meta}>{entry}</span>}
      {finished && (e.finish_time_ms !== null || e.overall_rank !== null) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 14px', marginBlockStart: 4, fontSize: 12.5 }}>
          {e.finish_time_ms !== null && (
            <span data-testid="legacy-event-time" style={{ fontWeight: 700 }}>
              {t('history.events.time', { time: formatFinishTime(e.finish_time_ms) })}
            </span>
          )}
          {e.overall_rank !== null && e.field_size !== null && (
            <span data-testid="legacy-event-rank">{t('history.events.rank', { rank: n(e.overall_rank), field: n(e.field_size) })}</span>
          )}
          {e.result_rows > 1 && (
            <span data-testid="legacy-event-results" style={{ color: 'var(--tru-text-tertiary)' }}>
              {t('history.events.resultsCount', { count: n(e.result_rows) })}
            </span>
          )}
        </div>
      )}
    </li>
  )
}

export function LegacyEventsDrillDown({ env = import.meta.env }: { env?: Record<string, string | undefined> }) {
  const { t } = useLocale()
  const [open, setOpen] = useState(false)
  const [state, setState] = useState<State>({ kind: 'closed' })
  const base = myApiBase(env)
  if (!legacyHistoryEventsEnabled(env) || !base) return null

  const load = () => {
    const session = loadSession()
    if (!session) {
      setState({ kind: 'unavailable' })
      return
    }
    setState({ kind: 'loading' })
    void fetchMyLegacyEvents(session.idToken, base).then((r) => setState(r))
  }

  const toggle = () => {
    const next = !open
    setOpen(next)
    // First open (or after a failure) fetches; a loaded list is kept.
    if (next && (state.kind === 'closed' || state.kind === 'unavailable')) load()
  }

  return (
    <div data-testid="legacy-events" style={{ marginBlockStart: 12 }}>
      <button type="button" data-testid="legacy-events-toggle" aria-expanded={open} onClick={toggle} style={linkButton}>
        {t(open ? 'history.events.hide' : 'history.events.open')}
      </button>
      {open && (
        <div data-testid="legacy-events-panel" aria-live="polite">
          {state.kind === 'loading' && <p data-testid="legacy-events-loading" style={quiet}>{t('history.events.loading')}</p>}
          {state.kind === 'unavailable' && (
            <p data-testid="legacy-events-unavailable" style={quiet}>
              {t('history.events.unavailable')}{' '}
              <button type="button" data-testid="legacy-events-retry" onClick={load} style={linkButton}>{t('history.events.retry')}</button>
            </p>
          )}
          {state.kind === 'ok' && state.events.length === 0 && (
            <p data-testid="legacy-events-empty" style={quiet}>{t('history.events.empty')}</p>
          )}
          {state.kind === 'ok' && state.events.length > 0 && (
            <ul data-testid="legacy-events-list" style={{ listStyle: 'none', margin: '10px 0 0', padding: 0 }}>
              {state.events.map((e, i) => (
                // Position key: no id is on the wire (privacy), and the list
                // is fixed for the life of the panel.
                <EventItem key={i} e={e} t={t} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
